import React, { useEffect, useRef, useState } from 'react';
import { notesApi } from '../../lib/notesApi';

/**
 * Lightweight @mention popover. Triggered by NoteComposer when the cursor
 * is inside a `@xyz` token. Owner controls visibility + the query string.
 */
export const MentionPopover = ({
  open,
  query,
  accountId,
  onPick,
  onClose,
  anchorRect,
}) => {
  const [users, setUsers] = useState([]);
  const [active, setActive] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await notesApi.searchUsers({ account_id: accountId, q: query, limit: 6 });
        if (!cancelled) {
          setUsers(data?.users || []);
          setActive(0);
        }
      } catch {
        if (!cancelled) setUsers([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, query, accountId]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActive((i) => Math.min(i + 1, users.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActive((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        if (users[active]) {
          e.preventDefault();
          onPick?.(users[active]);
        }
      } else if (e.key === 'Escape') {
        onClose?.();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, users, active, onPick, onClose]);

  if (!open || !users?.length) return null;

  const top = (anchorRect?.bottom ?? 0) + 4;
  const left = (anchorRect?.left ?? 0);

  return (
    <div
      ref={ref}
      data-testid="notes-mention-popover"
      style={{ position: 'fixed', top, left, zIndex: 1100 }}
      className="w-[260px] rounded-md border border-slate-200 bg-white shadow-lg overflow-hidden"
    >
      {users.map((u, i) => (
        <button
          key={u.id}
          type="button"
          onMouseEnter={() => setActive(i)}
          onMouseDown={(e) => { e.preventDefault(); onPick?.(u); }}
          data-testid="notes-mention-option"
          className={
            'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs ' +
            (i === active ? 'bg-teal-50 text-teal-900' : 'hover:bg-slate-50 text-slate-800')
          }
        >
          <span className="truncate font-medium">{u.name || u.email}</span>
          <span className="truncate text-[10px] text-slate-400">{u.email}</span>
        </button>
      ))}
    </div>
  );
};

export default MentionPopover;
